import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, Users, Phone, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Loading, ErrorState, EmptyState } from "@/components/States";

export default function CustomersList() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true); setError(null);
    try { setCustomers(await api("/customers")); } catch (e) { setError(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return customers;
    return customers.filter((c) => [c.name, c.email, c.phone, c.address].filter(Boolean).some((f) => f.toLowerCase().includes(t)));
  }, [customers, q]);

  return (
    <div>
      <PageHeader title="Customers" subtitle={`${customers.length} total`}
        actions={<Link to="/customers/new" data-testid="new-customer-btn" className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 text-sm font-bold text-white transition-transform hover:scale-105"><Plus className="h-4 w-4" />New Customer</Link>} />

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, phone or email…" data-testid="customer-search"
          className="w-full rounded-lg border border-input bg-card py-2.5 pl-10 pr-3 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
      </div>

      {loading ? <Loading /> : error ? <ErrorState message={error} onRetry={load} /> : filtered.length === 0 ? (
        <EmptyState icon={Users} title="No customers found" description={q ? "Try a different search." : "Add your first customer to get started."}
          action={!q && <Link to="/customers/new" className="rounded-lg bg-brand px-4 py-2.5 text-sm font-bold text-white">New Customer</Link>} />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((c) => (
            <button key={c.id} onClick={() => navigate(`/customers/${c.id}`)} data-testid={`customer-card-${c.id}`}
              className="flex items-start gap-3 rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-brand hover:bg-brand-tertiary">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-tertiary text-base font-extrabold text-brand">{(c.name || "?").charAt(0).toUpperCase()}</div>
              <div className="min-w-0">
                <p className="truncate font-bold text-midnight">{c.name}</p>
                {c.phone && <p className="mt-0.5 flex items-center gap-1.5 truncate text-xs text-muted-foreground"><Phone className="h-3 w-3" />{c.phone}</p>}
                {c.email && <p className="flex items-center gap-1.5 truncate text-xs text-muted-foreground"><Mail className="h-3 w-3" />{c.email}</p>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
